import { ogImageMeta, hubOgKey, bestOgKey } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { fetchTagHub } from "@/lib/library-hubs.functions";
import { tagIntro, HUB_MIN_ITEMS } from "@/lib/library-hubs";
import {
  LibraryHubPage,
  itemListJsonLd,
  breadcrumbJsonLd,
  datasetJsonLd,
} from "@/components/library-hub";
import { SiteFooter } from "@/components/site-footer";

export const Route = createFileRoute("/library/t/$tag")({
  loader: async ({ params }) => {
    try {
      return { hub: await fetchTagHub({ data: { tag: params.tag } }) };
    } catch {
      return { hub: null };
    }
  },
  head: ({ params, loaderData }) => {
    const hub = loaderData?.hub ?? null;
    const url = `https://vibing.inc/library/t/${params.tag}`;
    if (!hub) {
      return {
        meta: [
          { title: "Tag not found — The Vibeary" },
          { name: "robots", content: "noindex" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = `${hub.tag} — ${hub.count} curated tools | The Vibeary`;
    const desc = tagIntro(hub.tag, hub.count).slice(0, 155);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        ...ogImageMeta("library", `Share card: ${hub.tag} tools in the Hidden Gems Library`),
        { name: "twitter:card", content: "summary_large_image" },
        ...(hub.count < HUB_MIN_ITEMS
          ? [{ name: "robots", content: "noindex,follow" }]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: itemListJsonLd(hub.tag, url, hub.gems),
        },
        {
          type: "application/ld+json",
          children: datasetJsonLd({
            name: `${hub.tag} — The Vibing Library`,
            description: desc,
            url,
            count: hub.count,
            keywords: [hub.tag, "tools", "curated"],
          }),
        },
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", item: "https://vibing.inc/" },
            { name: "The Vibeary", item: "https://vibing.inc/library" },
            { name: hub.tag, item: url },
          ]),
        },
      ],
    };
  },
  component: TagHub,
});

function TagHub() {
  const { hub } = Route.useLoaderData();
  if (!hub) {
    return (
      <div className="min-h-dvh bg-background text-foreground">
        <main id="main" className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl">Tag not found</h1>
          <Link to="/library" className="mt-4 inline-block text-muted-foreground underline">
            Back to the library
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }
  return (
    <LibraryHubPage
      eyebrow="The Vibeary — tag"
      title={hub.tag}
      intro={tagIntro(hub.tag, hub.count)}
      gems={hub.gems}
      crumbs={[
        { label: "Home", to: "/" },
        { label: "The Vibeary", to: "/library" },
        { label: hub.tag },
      ]}
    />
  );
}
